import { Link, linkOptions } from "@tanstack/react-router";
import React from "react";

import { CookieSettingsButton } from "~/components/consent/cookie-settings-button";
import IcfBpLogo from "~/img/icfbp-logo-mono.svg?react";
import ImageDcmLogoHorizontalWhiteUrl from "~/img/logo/dcm-logo-horizontal-white.png";
import { cn } from "~/lib/cn";
import { trackNavClick } from "~/lib/monitoring/track-event";
import { BRAND, BRAND_POSTAL_ADDRESS_LINES } from "~/lib/shared/brand";

type FooterLink = { label: string } & (
  | {
      to: string;
      params?: Record<string, string>;
      search?: Record<string, string>;
    }
  | { href: `mailto:${string}` }
  /** Opens the cookie settings dialog in place. */
  | { cookieSettings: true }
);

type FooterGroup = { title: string; links: FooterLink[] };

const pageLink = (name: string) =>
  linkOptions({ to: "/page/$name", params: { name } });

const FOOTER_GROUPS: FooterGroup[] = [
  {
    title: "Find a Coach",
    links: [
      { label: "Get Matched", ...linkOptions({ to: "/discovery" }) },
      { label: "Browse Coaches", ...linkOptions({ to: "/coaches" }) },
      {
        label: "Wheel of Life assessment",
        ...linkOptions({ to: "/wheel-of-life" }),
      },
      { label: "How It Works", ...linkOptions({ to: "/how-it-works" }) },
    ],
  },
  {
    title: "Guidance",
    links: [
      {
        label: "Coaching Intelligence Hub",
        ...linkOptions({ to: "/coaching-intelligence" }),
      },
      {
        label: "What are you facing?",
        ...linkOptions({ to: "/coaching-for" }),
      },
      { label: "Articles", ...linkOptions({ to: "/articles" }) },
      { label: "Success Stories", ...linkOptions({ to: "/stories" }) },
      { label: "Frameworks", ...linkOptions({ to: "/frameworks" }) },
      {
        label: "Training Programs",
        ...linkOptions({ to: "/training-programs" }),
      },
      { label: "Organizations", ...linkOptions({ to: "/organizations" }) },
    ],
  },
  {
    title: "For Coaches",
    links: [
      {
        label: "6-Figure Readiness Score",
        ...linkOptions({ to: "/6-figure-readiness-score" }),
      },
      { label: "Join as a Coach", ...pageLink("coach") },
      { label: "NOVA", ...pageLink("nova") },
      {
        label: "Business Guides",
        ...linkOptions({ to: "/coaching-business" }),
      },
      {
        label: "Playbooks",
        ...linkOptions({ to: "/coaching-business/playbooks" }),
      },
      {
        label: "Coach Business Builder",
        ...pageLink("coach-business-builder"),
      },
      { label: "Coaching OS", ...pageLink("coaching-os") },
      { label: "Plans & Pricing", ...pageLink("pricing") },
      {
        label: "Coach Login",
        // `_auth` sends visitors who are already signed in to `redirect`, and
        // otherwise to the member dashboard.
        ...linkOptions({
          to: "/login",
          search: { redirect: "/my-coaching/clients/dashboard" },
        }),
      },
    ],
  },
  {
    title: "For Organizations",
    links: [{ label: "Partner With Us", ...pageLink("partner") }],
  },
  {
    title: "Company",
    links: [
      { label: "About", ...linkOptions({ to: "/about" }) },
      { label: "What We Offer", ...linkOptions({ to: "/what-we-offer" }) },
      {
        label: "Dream Coach Match vs. BetterUp",
        ...linkOptions({ to: "/dcm-vs-betterup" }),
      },
      { label: "Summit", ...linkOptions({ to: "/summit" }) },
      { label: "Vision", ...linkOptions({ to: "/vision" }) },
      { label: "Contact", href: "mailto:info@dreamcoachmatch.com" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy", ...pageLink("privacy-policy") },
      { label: "Terms", ...pageLink("terms-of-services") },
      { label: "SMS Terms", ...pageLink("sms-terms") },
      { label: "Cookie settings", cookieSettings: true },
    ],
  },
];

const footerLinkClassName = "text-gray-400 transition-colors hover:text-white";

const FooterGroupLink = ({ link }: { link: FooterLink }) => {
  if ("cookieSettings" in link) {
    return <CookieSettingsButton className={footerLinkClassName} />;
  }

  // The rendered href resolves `/page/$name` to the page the visitor opened.
  const onClick = (event: React.MouseEvent<HTMLAnchorElement>): void =>
    trackNavClick({
      location: "footer",
      label: link.label,
      destination: event.currentTarget.getAttribute("href") ?? "",
    });

  if ("href" in link) {
    return (
      <a href={link.href} onClick={onClick} className={footerLinkClassName}>
        {link.label}
      </a>
    );
  }

  return (
    <Link
      to={link.to}
      params={link.params}
      search={link.search}
      onClick={onClick}
      className={footerLinkClassName}
    >
      {link.label}
    </Link>
  );
};

/** Site footer; the diagnostic variant is a short dark footer with legal links. */
export const Footer = ({
  variant = "default",
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  variant?: "default" | "diagnostic";
}) => {
  const currentYear = new Date().getFullYear();

  if (variant === "diagnostic") {
    return (
      <footer
        {...props}
        className={cn("bg-gray-950 py-10 text-gray-400", props.className)}
      >
        <div className="mx-auto max-w-7xl px-4 text-center text-sm xl:px-0">
          <img
            src={ImageDcmLogoHorizontalWhiteUrl}
            alt="Dream Coach Match"
            className="mx-auto mb-4 h-7 w-auto"
          />
          <p>The global home for coaching</p>
          <nav aria-label="Legal" className="mt-3 flex justify-center gap-4">
            <Link
              to="/page/$name"
              params={{ name: "privacy-policy" }}
              className="underline-offset-4 hover:text-white hover:underline"
            >
              Privacy Policy
            </Link>
            <Link
              to="/page/$name"
              params={{ name: "terms-of-services" }}
              className="underline-offset-4 hover:text-white hover:underline"
            >
              Terms of Service
            </Link>
            <CookieSettingsButton className="underline-offset-4 hover:text-white hover:underline" />
          </nav>
        </div>
      </footer>
    );
  }

  return (
    <footer
      {...props}
      className={cn("z-10 bg-gray-950 py-12 text-gray-200", props.className)}
    >
      <div className="container mx-auto grid max-w-screen-xl gap-12 px-4 lg:grid-cols-[18rem_minmax(0,1fr)] lg:gap-16">
        <div className="flex flex-col items-start gap-4">
          <Link to="/" aria-label="Dream Coach Match home">
            <img
              src={ImageDcmLogoHorizontalWhiteUrl}
              alt="DreamCoachMatch Logo"
              style={{ height: 32, width: "auto" }}
            />
          </Link>
          <p className="text-sm text-gray-400 italic">{BRAND.slogan}</p>
          <div className="max-w-sm">
            <h2 className="mb-2 font-heading text-sm font-medium text-gray-100">
              About Dream Coach Match
            </h2>
            <p className="text-sm leading-relaxed text-gray-400">
              {BRAND.description}
            </p>
          </div>
          <IcfBpLogo
            role="img"
            aria-label="International Coaching Federation Business Solutions Partner badge"
            style={{ height: "48px" }}
            className="mt-4 text-gray-100"
          />
          <div className="mt-4 text-sm text-gray-400">
            © {currentYear} DreamCoachMatch. All rights reserved.
          </div>
          <address className="text-sm text-gray-400 not-italic">
            <div>{BRAND.name}</div>
            {BRAND_POSTAL_ADDRESS_LINES.map((line) => (
              <div key={line}>{line}</div>
            ))}
            <a href="tel:+14254413867" className="hover:text-white">
              +1 (425) 441-3867
            </a>
          </address>
        </div>
        <nav
          aria-label="Footer"
          className="grid grid-cols-2 content-start gap-x-6 gap-y-10 sm:grid-cols-3"
        >
          {FOOTER_GROUPS.map((group) => (
            <div key={group.title} className="min-w-0">
              <h2 className="mb-4 font-heading text-sm font-medium text-gray-100">
                {group.title}
              </h2>
              <ul className="space-y-3 text-sm">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <FooterGroupLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </footer>
  );
};
